/**
 * Гостевой счёт перед печатью.
 *
 * Показываем ровно то, что выйдет из принтера: моноширинный блок
 * с шапкой, позициями и итогом. Официант сверяет со столом, гость
 * получит эту же бумагу — расхождению взяться неоткуда.
 *
 * Окно по центру, а не нижним листом: гость смотрит в экран вместе
 * с официантом, и бумага должна лежать перед обоими, а не выезжать
 * из-под руки.
 *
 * Одно окно на двоих. У официанта внизу «Закрыть счёт»: он печатает,
 * отдаёт бумагу гостю и зовёт кассира. У кассира то же самое плюс
 * «Оплатить» — он закрывает счёт здесь же, не переходя на экран
 * с теми же цифрами.
 */
import { useState } from 'react';
import { useKeyboard } from './useKeyboard';
import { formatMoney, lineSum, type OrderState } from '@dastarhan/core';

export type BillTotals = {
  subtotal: number;
  discount: number;
  service: number;
  servicePct: number;
  total: number;
};

export function BillDialog({
  order, table, totals, promos, guestName, canPay, ownerName,
  placeName, printedAt, stale, cashierName, freshCount, onSplit, onDiscount, onCard, cardLabel, onPrint, onClose, onPay, onGiveBack, canUnbill, billed, onUnbill, onComment, onBack,
}: {
  order: OrderState;
  table: { name: string } | null;
  totals: BillTotals;
  /** акции, карта, лояльность — каждая своей строкой с названием */
  promos: { name: string; amount: number }[];
  guestName?: string | null;
  /** кассир видит «Оплатить»; официант — «Закрыть счёт» */
  canPay: boolean;
  /** имя официанта, если стол взят чужой — тогда можно вернуть */
  ownerName?: string | null;
  placeName: string;
  /** во сколько печатали счёт; null — ещё не печатали */
  printedAt?: string | null;
  /** состав изменился после печати — у гостя бумага с другой суммой */
  stale?: boolean;
  cashierName: string;
  /** сколько строк ещё не ушло на кухню — их не приготовят */
  freshCount: number;
  /** разделить счёт между гостями: частая просьба компании */
  onSplit?: (() => void) | null;
  /** скидка и карта — только кассиру: он оплачивает и решает */
  onDiscount?: (() => void) | null;
  onCard?: (() => void) | null;
  /** имя гостя по приложенной карте */
  cardLabel?: string | null;
  onPrint: () => void;
  /**
   * Закрыть счёт: напечатать И пометить стол ждущим денег.
   *
   * Отличается от простой печати: гость может попросить счёт просто
   * посмотреть, и тогда стол остаётся в работе. А закрытие — решение
   * официанта: он отдаёт бумагу, идёт за деньгами и к столу
   * не возвращается
   */
  onClose: () => void;
  onPay: () => void;
  onGiveBack?: () => void;
  /** право вернуть счёт в работу — даётся в офисе */
  canUnbill?: boolean;
  /** счёт уже закрыт: только тогда есть что возвращать */
  billed?: boolean;
  onUnbill?: () => void;
  onComment: (text: string) => void;
  onBack: () => void;
}) {
  const live = order.items.filter((i) => !i.removed);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(order.comment ?? '');
  // Экранная клавиатура: на Windows-планшете системная не всплывает
  const kb = useKeyboard(note, setNote, 'text');

  // Сколько компания за столом: официант видит, пора ли предлагать
  // десерт или гости уже собираются
  const mins = Math.max(0, Math.round((Date.now() - new Date(order.openedAt).getTime()) / 60_000));
  const sitting = mins < 60 ? `${mins} мин` : `${Math.floor(mins / 60)} ч ${mins % 60} мин`;

  const when = new Date(order.openedAt).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

  return (
    <div className="dialog-backdrop center" onClick={onBack}>
      <div className="dialog bill-sheet" onClick={(e) => e.stopPropagation()}>
        <header className="bill-head">
          <span className="eyebrow">{canPay ? 'Оплата' : 'Закрытие счёта'}</span>
          <button className="bill-x" onClick={onBack} aria-label="Закрыть">×</button>
        </header>
        <h1>{table ? `Стол ${table.name}` : `Чек №${order.number}`}</h1>

        {/* Не ушло на кухню — самое опасное перед закрытием: гость
            получит счёт за блюдо, которое никто не готовил */}
        {freshCount > 0 && (
          <div className="bill-alarm">
            <b>{freshCount} поз. не отправлено на кухню</b>
            <span>
              Эти блюда не готовят. Закройте счёт только если гость
              от них отказался — иначе вернитесь и нажмите «Заказать».
            </span>
          </div>
        )}

        {/* Сводка: сколько сидят и сколько позиций. Официант решает
            по ней, пора ли предлагать десерт */}
        <div className="bill-facts">
          <span>За столом <b>{sitting}</b></span>
          <span>Позиций <b>{live.length}</b></span>
          {order.openedBy && <span>Официант <b>{order.openedBy.name}</b></span>}
        </div>

        {/* Вид ленты: моноширинный, выровненный по колонкам. Гость
            получит ровно это, и спорить будет не о чем */}
        <div className="bill-paper">
          <div className="bp-title">ГОСТЕВОЙ СЧЁТ</div>
          <div className="bp-place">{placeName}</div>

          <div className="bp-rule" />

          <div className="bp-row">
            <span>Счёт</span><b>{String(order.number).padStart(4, '0')}</b>
          </div>
          <div className="bp-row"><span>Дата</span><b>{when}</b></div>
          {table && <div className="bp-row"><span>Стол</span><b>{table.name}</b></div>}
          <div className="bp-row">
            <span>Официант</span>
            <b>{order.openedBy?.name ?? cashierName}</b>
          </div>
          {guestName && <div className="bp-row"><span>Гость</span><b>{guestName}</b></div>}

          <div className="bp-rule" />

          {live.map((i) => (
            <div key={i.itemId} className="bp-item">
              <div className="bp-name">{i.name}</div>
              {i.modifiers.length > 0 && (
                <div className="bp-mods">{i.modifiers.map((m) => m.name).join(', ')}</div>
              )}
              <div className="bp-row">
                <span>{i.qty} × {formatMoney(i.unitPrice)}</span>
                <b>{formatMoney(lineSum(i))}</b>
              </div>
            </div>
          ))}

          <div className="bp-rule" />

          {(promos.length > 0 || totals.discount > 0 || totals.service > 0) && (
            <div className="bp-row">
              <span>Подытог</span><b>{formatMoney(totals.subtotal)}</b>
            </div>
          )}
          {promos.map((p) => (
            <div key={p.name} className="bp-row minus">
              <span>{p.name}</span><b>−{formatMoney(p.amount)}</b>
            </div>
          ))}
          {totals.discount > 0 && (
            <div className="bp-row minus">
              <span>Скидка</span><b>−{formatMoney(totals.discount)}</b>
            </div>
          )}
          {totals.service > 0 && (
            <div className="bp-row">
              <span>Обслуживание {totals.servicePct}%</span>
              <b>+{formatMoney(totals.service)}</b>
            </div>
          )}

          {/* Число позиций рядом с итогом: гость пересчитывает блюда
              глазами, и цифра помогает сойтись быстрее */}
          <div className="bp-row bp-count">
            <span>Позиций</span>
            <b>{live.reduce((n, i) => n + i.qty, 0)}</b>
          </div>

          <div className="bp-total">
            <span>Итого</span>
            <b>{formatMoney(totals.total)}</b>
          </div>
        </div>

        {/* Комментарий ко всему чеку: «день рождения», «жалоба по подаче».
            Его увидит кассир при оплате и владелец в отчёте */}
        <div className="bill-note">
          <div className="bn-text">
            <b>Комментарий к чеку</b>
            <span>{order.comment || 'Не указан'}</span>
          </div>
          <button className="btn small" onClick={() => setNoteOpen(true)}>
            {order.comment ? 'Изменить' : 'Добавить'}
          </button>
        </div>

        {noteOpen && (
          <div className="bill-note-edit">
            <input {...kb.props} value={note} maxLength={120}
              placeholder="день рождения, просили не беспокоить"
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { onComment(note.trim()); setNoteOpen(false); }
                if (e.key === 'Escape') setNoteOpen(false);
              }} />
            <button className="btn accent"
              onClick={() => { onComment(note.trim()); setNoteOpen(false); }}>
              Готово
            </button>
          </div>
        )}
        {noteOpen && kb.node}

        {/* Возврат счёта: гость посмотрел бумагу и решил дозаказать.
            Официант видит это раньше кассира — он у стола */}
        {canUnbill && billed && onUnbill && (
          <button className="btn unbill-row" onClick={onUnbill}>
            ↩ Вернуть стол официанту
          </button>
        )}

        {ownerName && onGiveBack && (
          <button className="btn give-back" onClick={onGiveBack}>
            ↩ Вернуть официанту · {ownerName}
          </button>
        )}

        {/* Разделить счёт: компания просит об этом постоянно, и делать
            это надо ДО печати — потом придётся печатать заново */}
        {/* Скидка и карта — из этого же окна: кассир видит счёт целиком
            и решает, а не бегает между экранами с теми же цифрами.
            Официанту не показываем: скидки не его решение */}
        {canPay && (onDiscount || onCard) && (
          <div className="bill-extras">
            {onCard && (
              <button className="btn" onClick={onCard}>
                {cardLabel ? `Карта: ${cardLabel}` : 'Карта гостя'}
              </button>
            )}
            {onDiscount && (
              <button className="btn" onClick={onDiscount}>
                {totals.discount > 0 ? `Скидка ${formatMoney(totals.discount)}` : 'Дать скидку'}
              </button>
            )}
          </div>
        )}

        {onSplit && live.length > 1 && (
          <button className="btn bill-split" onClick={onSplit}>
            ⑃ Разделить счёт между гостями
          </button>
        )}

        {/* Счёт уже печатали, а состав с тех пор изменился: гость
            держит на руках бумагу с другой суммой. Молчать об этом
            нельзя — разговор о деньгах начнётся с недоверия */}
        {printedAt && (
          <p className={`bill-printed ${stale ? 'stale' : ''}`}>
            {stale
              ? `Счёт печатали в ${printedAt} — состав изменился. Напечатайте заново.`
              : `Счёт напечатан в ${printedAt}`}
          </p>
        )}

        <div className="bill-actions">
          <button className="btn" onClick={onBack}>Отмена</button>
          <button className="btn accent" onClick={onPrint}>
            <i aria-hidden>⎙</i> Печать
          </button>
          {canPay
            ? <button className="btn primary" onClick={onPay}>
                <i aria-hidden>✓</i> Оплатить
              </button>
            : <button className="btn primary" onClick={onClose}>
                <i aria-hidden>✓</i> Закрыть счёт
              </button>}
        </div>

        {!canPay && (
          <p className="bill-note-hint">
            Напечатайте счёт, отдайте гостю и передайте деньги кассиру —
            он закроет стол со своего терминала.
          </p>
        )}
      </div>
    </div>
  );
}
